import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import { BiVizRealtimePresenceDatasource } from '../sql-maps/biviz-realtime-presence-sql-map';
import { BiVizRealtimeStatsDatasource } from '../sql-maps/biviz-realtime-stats-sql-map';

export interface RealtimeStatsPurchaseCompleteDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

export class RealtimeStatsPurchaseCompleteDataLoaderFactory extends Factory<
    DataLoader,
    RealtimeStatsPurchaseCompleteDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'RealtimeStatsPurchaseCompleteDataLoaderFactory';

    getInstance(
        options: RealtimeStatsPurchaseCompleteDataLoaderFactoryOptions
    ) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            // cloone queryContext to avoid modifying the original object
            queryContext = structuredClone(queryContext);

            // remove REALTIME_PURCHASE_ERROR from queryContext
            // this DataLoader is not supporting REALTIME_PURCHASE_ERROR because no metric depends on it
            queryContext.datasourceColumns =
                queryContext.datasourceColumns.filter(
                    (col) => col.id !== 'REALTIME_PURCHASE_ERROR'
                );

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const biVizRealtimeStats = BiVizRealtimeStatsDatasource;

                // identify columns not supported in datasource
                const biVizRealtimeStatsDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        biVizRealtimeStats.sqlMap.columns
                    );

                let latestHourSqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_hours_up_to_latest is true, then we should filter hours to be up to the latest hour
                    if (queryContext.settings.realtimeHoursUpToLatest) {
                        const latest_hour = await DataLoader.runQuery(
                            `SELECT EXTRACT(HOUR FROM MAX(time_hour)) AS latest_hour FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                biVizRealtimeStats.sqlMap.dataset,
                                biVizRealtimeStats.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );

                        return {
                            where: [
                                BiVizRealtimeStatsDatasource.compileWhereSQLHoursUpToLatest(
                                    latest_hour.result[0].latest_hour
                                ),
                            ],
                        };
                    }
                    return;
                })();

                let currentWeekdaySqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_current_weekday is true, then we should filter weekday to be the current weekday
                    if (queryContext.settings.realtimeCurrentWeekday) {
                        const current_weekday = await DataLoader.runQuery(
                            `SELECT FORMAT_DATETIME("%A", MAX(time_hour)) AS current_weekday FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                biVizRealtimeStats.sqlMap.dataset,
                                biVizRealtimeStats.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );
                        queryContext.settings.realtimeCurrentWeekday =
                            current_weekday.result[0].current_weekday;

                        return {
                            where: [
                                BiVizRealtimeStatsDatasource.compileWhereSQLCurrentWeekday(
                                    current_weekday.result[0].current_weekday
                                ),
                            ],
                        };
                    }
                    return;
                })();

                const customSQLTokens: Partial<ICompiledSQLTokens> =
                    mergeSqlTokensArray(
                        [latestHourSqlToken, currentWeekdaySqlToken]
                            // filter out undefined values
                            .filter((x) => x) as Partial<ICompiledSQLTokens>[]
                    );

                const biVizRealtimeStatsDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        biVizRealtimeStatsDsColumns.missing
                    );

                const purchaseCompiledSQL =
                    biVizRealtimeStats.compileSQLStatements(
                        biVizRealtimeStatsDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const biVizRealtimePresence = BiVizRealtimePresenceDatasource;

                const biVizRealtimePresenceDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        biVizRealtimePresence.sqlMap.columns
                    );

                const biVizRealtimePresenceDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        biVizRealtimePresenceDsColumns.missing
                    );

                const presenceCompiledSQL =
                    biVizRealtimePresence.compileSQLStatements(
                        biVizRealtimePresenceDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const getMetricSQL = (
                    metricSQLSource: string,
                    metricSQL: string,
                    normalizer: string,
                    normalizedMode: IQueryContext['settings'][QueryContextSettingID.REALTIME_NORMALIZE_METRICS]
                ): string => {
                    switch (normalizedMode) {
                        case 'none':
                            return metricSQL;
                        case 'dau':
                            return `SAFE_DIVIDE(${metricSQLSource}.${metricSQL}, ${normalizer}) AS ${metricSQL}`;
                        default:
                            throw new Error(
                                `Unknown normalization mode: ${normalizedMode}`
                            );
                    }
                };

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH purchase AS
(
  SELECT
    ${purchaseCompiledSQL.datetimeTrunc()} AS datetime
    , ${purchaseCompiledSQL.splitSelect}
    ${purchaseCompiledSQL.cohortSplitSelect}
    NULLIF(SUM(PurchaseComplete_count), 0) AS PurchaseComplete_count
    , NULLIF(SUM(PurchaseComplete_users_count), 0) AS PurchaseComplete_users_count
    , NULLIF(SUM(ProductConsumed_count), 0) AS ProductConsumed_count
    , NULLIF(SUM(ConsumeFailed_count), 0) AS ConsumeFailed_count
    , NULLIF(SUM(ProductRestored_count), 0) AS ProductRestored_count
    , SUM(PurchaseComplete_count + ProductRestored_count - ProductConsumed_count) AS UnconsumedPurchases_count
    , SAFE_DIVIDE(
      SUM(PurchaseComplete_count - ProductConsumed_count)
      , SUM(PurchaseComplete_count)
    ) AS UnconsumedPurchases_rate
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      biVizRealtimeStats.sqlMap.dataset,
      biVizRealtimeStats.sqlMap.tableId
  )}\` ds
  ${purchaseCompiledSQL.where}
  ${SqlComposer.GROUP_BY([
      `datetime`,
      purchaseCompiledSQL.splitGroup,
      purchaseCompiledSQL.cohortSplitGroup,
  ])}
)

, presence AS
(
  SELECT 
    ${presenceCompiledSQL.datetimeTrunc()} AS datetime
    , ${presenceCompiledSQL.splitSelect}
    NULLIF( SUM( installers_count), 0) AS installers_count
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      biVizRealtimePresence.sqlMap.dataset,
      biVizRealtimePresence.sqlMap.tableId
  )}\` ds
  ${presenceCompiledSQL.where}
  ${SqlComposer.GROUP_BY([`datetime`, presenceCompiledSQL.splitGroup])}
)

SELECT 
  purchase.datetime
  ${purchaseCompiledSQL.splitSelect ? ', purchase.split_group' : ''}
  , ${getMetricSQL(
      'purchase',
      `PurchaseComplete_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  , ${getMetricSQL(
      'purchase',
      `PurchaseComplete_users_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  , ${getMetricSQL(
      'purchase',
      `ProductConsumed_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  , ${getMetricSQL(
      'purchase',
      `UnconsumedPurchases_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  , purchase.UnconsumedPurchases_rate
  , ${getMetricSQL(
      'purchase',
      `ConsumeFailed_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  , ${getMetricSQL(
      'purchase',
      `ProductRestored_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  , presence.installers_count
FROM purchase
LEFT JOIN presence
  ON purchase.datetime = presence.datetime
${
    presenceCompiledSQL.splitGroupBy
        ? `-- presence does not contains any non-cohort columns, so we join on split_group
      AND purchase.cohort_split_group = presence.split_group`
        : ''
}
${SqlComposer.ORDER_BY(['datetime', purchaseCompiledSQL.splitGroup])}
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'datetime',
                    queryContext.settings[
                        QueryContextSettingID.DATETIME_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: biVizRealtimeStatsDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        biVizRealtimeStatsDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
